import { ClientEndpointType, DrakewellClientEndpoint, EmailClientEndpoint, FabricClientEndpoint, JsonClientEndpoint, LuxcomClientEndpoint, MS2ClientEndpoint, NtcipDmsClientEndpoint, WebRelayClientEndpoint } from "@app/shared";
import { InputRow, Select } from "@app/dashboard/src/components/shared/inputs";
import { clientEndpointTypeOptions } from "../endpointTypeLabels";
import { EditEmailClientEndpoint } from "./EditEmailClientEndpoint";
import { EditJsonClientEndpoint } from "./EditJsonClientEndpoint";
import { EditLuxcomClientEndpoint } from "./EditLuxcomClientEndpoint";
import { EditNtcipDmsClientEndpoint } from "./EditNtcipDmsClientEndpoint";
import { EditWebRelayClientEndpoint } from "./EditWebRelayClientEndpoint";
import { EditMS2ClientEndpoint } from "./EditMS2ClientEndpoint";
import { EditDrakewellClientEndpoint } from "./EditDrakewellClientEndpoint";
import { EditFabricClientEndpoint } from "./EditFabricClientEndpoint";

export type ClientEndpointTypes = EmailClientEndpoint | JsonClientEndpoint | LuxcomClientEndpoint | NtcipDmsClientEndpoint | WebRelayClientEndpoint | FabricClientEndpoint;

export type ClientEndpointEditorProps = {
    endpoint: ClientEndpointTypes | undefined;
    onChange: (endpoint: ClientEndpointTypes | undefined) => any;
    offlineMode?: boolean;
    // Endpoint types the caller wants shown but not selectable, mapped to the reason (rendered as the option's hover text).
    disabledTypes?: Partial<Record<ClientEndpointType, string>>;
    // Dashboard origin for API-docs links, for hosts that don't bundle the docs page (WebConfig).
    // See ClientEndpointTable / EndpointApiDocs/paths.ts.
    docsOrigin?: string;
};

const getEditor = (props: ClientEndpointEditorProps) => {
    const $type = props.endpoint?.endpointType;
    if ($type === ClientEndpointType.Email) return <EditEmailClientEndpoint {...props} />;
    if ($type === ClientEndpointType.Json) return <EditJsonClientEndpoint {...props} />;
    if ($type === ClientEndpointType.Luxcom) return <EditLuxcomClientEndpoint {...props} />;
    if ($type === ClientEndpointType.NTCIP_DMS) return <EditNtcipDmsClientEndpoint {...props} />;
    if ($type === ClientEndpointType.WebRelay) return <EditWebRelayClientEndpoint {...props} />;
    if ($type === ClientEndpointType.MS2) return <EditMS2ClientEndpoint {...props} />;
    if ($type === ClientEndpointType.Drakewell) return <EditDrakewellClientEndpoint {...props} />;
    if ($type === ClientEndpointType.Fabric) return <EditFabricClientEndpoint {...props} />;
    return <></>;
};

export const ClientEndpointEditor = (props: ClientEndpointEditorProps) => {
    if (!props || !props.endpoint) return <>No endpoint to edit. Please try again</>;
    return (
        <>
            <InputRow>
                <Select
                    label="Endpoint Type"
                    emptyOptionText="Select Endpoint Type"
                    emptyOptionDisabled={false}
                    options={clientEndpointTypeOptions().map((o) => {
                        const value = parseInt(o.value) as ClientEndpointType;
                        // Gated types stay visible but unselectable, with the reason as hover text.
                        const reason = props.disabledTypes?.[value];
                        return reason
                            ? { label: `${o.label} (unavailable)`, value, disabled: true, title: reason }
                            : { label: o.label, value };
                    })}
                    disabled={!!props?.endpoint?.id}
                    value={props?.endpoint?.endpointType}
                    onChange={(v) => {
                        const newType = parseInt(v.currentTarget.value) as ClientEndpointType;
                        const typeMap: Record<number, string> = {
                            [ClientEndpointType.Json]: JsonClientEndpoint.$type,
                            [ClientEndpointType.Email]: EmailClientEndpoint.$type,
                            [ClientEndpointType.Luxcom]: LuxcomClientEndpoint.$type,
                            [ClientEndpointType.NTCIP_DMS]: NtcipDmsClientEndpoint.$type,
                            [ClientEndpointType.WebRelay]: WebRelayClientEndpoint.$type,
                            [ClientEndpointType.MS2]: MS2ClientEndpoint.$type,
                            [ClientEndpointType.Drakewell]: DrakewellClientEndpoint.$type,
                            [ClientEndpointType.Fabric]: FabricClientEndpoint.$type,
                        };
                        props?.onChange(props && props?.endpoint ? { ...props.endpoint, endpointType: newType, $type: typeMap[newType] } : undefined);
                    }}
                />
            </InputRow>
            {getEditor(props)}
        </>
    );
};
